import { Injectable, inject, signal } from '@angular/core';
import { $t, palette, updatePrimaryPalette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Nora from '@primeuix/themes/nora';
import { Observable, shareReplay, tap } from 'rxjs';

import { AppSettings, Branding, BrandColors, ColorKey, FontSize, PanelTheme, PresetName, ThemeMode } from '@/model/availability.model';
import { AvailabilityService } from './availability.service';
import { LayoutService } from '@/layout/service/layout.service';
import { mediaUrl } from '@/utils/room';

const PRESETS = { Aura, Lara, Nora } as const;
const PANEL_PREFS_KEY = 'ahla.panelPrefs';
const CUSTOMER_DARK_KEY = 'ahla.customerDark';

export const FONT_SIZES: Record<FontSize, string> = { sm: '13px', md: '14px', lg: '15px', xl: '16px' };

// تنظیمات نمایشی که هر کاربر (پرسنل/مدیر) روی دستگاه خودش انتخاب می‌کند؛ رنگ‌ها همیشه از مدیر است
export type PanelPrefs = Partial<PanelTheme>;

// مانند app.configurator: رنگ اصلی در حالت روشن/تیره
function presetExtension(primary: Record<string, string>, preset: PresetName) {
  const nora = preset === 'Nora';
  return {
    semantic: {
      primary,
      colorScheme: {
        light: {
          primary: nora
            ? { color: '{primary.600}', contrastColor: '#ffffff', hoverColor: '{primary.700}', activeColor: '{primary.800}' }
            : { color: '{primary.500}', contrastColor: '#ffffff', hoverColor: '{primary.600}', activeColor: '{primary.700}' },
          highlight: nora
            ? { background: '{primary.600}', focusBackground: '{primary.700}', color: '#ffffff', focusColor: '#ffffff' }
            : { background: '{primary.50}', focusBackground: '{primary.100}', color: '{primary.700}', focusColor: '{primary.800}' },
        },
        dark: {
          primary: nora
            ? { color: '{primary.500}', contrastColor: '{surface.900}', hoverColor: '{primary.400}', activeColor: '{primary.300}' }
            : { color: '{primary.400}', contrastColor: '{surface.900}', hoverColor: '{primary.300}', activeColor: '{primary.200}' },
          highlight: nora
            ? { background: '{primary.500}', focusBackground: '{primary.400}', color: '{surface.900}', focusColor: '{surface.900}' }
            : {
                background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                color: 'rgba(255,255,255,.87)',
                focusColor: 'rgba(255,255,255,.87)',
              },
        },
      },
    },
  };
}

function hexToHsl(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// «ملایم کردن و تعادل رنگ‌ها»: اشباع کمتر و روشنایی نزدیک‌تر به میانه تا رنگ‌ها کنار هم چشم را خسته نکنند
export function softenColor(hex: string): string {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const [h, s, l] = hexToHsl(hex);
  return hslToHex(h, s * 0.68, l + (0.52 - l) * 0.22);
}

// حالت سیستم‌عامل کاربر
function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

// مرورگر قدیمی (کلاس legacy-browser در index.html): همیشه حالت روشن
export function isLegacyBrowser(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('legacy-browser');
}

export function isDarkMode(mode: ThemeMode | undefined, legacyDark?: boolean): boolean {
  if (isLegacyBrowser()) return false;
  if (mode === 'system') return systemPrefersDark();
  if (mode === 'dark' || mode === 'light') return mode === 'dark';
  return !!legacyDark;
}

// اعلام صریح حالت رنگ به مرورگر؛ مرورگرهایی مثل Samsung Internet در غیر این صورت صفحه را خودکار تیره می‌کنند
// و رنگ‌های سامانه به‌هم می‌ریزد
export function applyColorScheme(dark: boolean) {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'color-scheme';
    document.head.appendChild(meta);
  }
  meta.content = dark ? 'dark' : 'only light';
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

export function resolveColor(colors: BrandColors, key: ColorKey, soften: boolean): string {
  const raw = key === 'black' ? '#111111' : key === 'white' ? '#ffffff' : (colors[key] ?? colors.primary);
  return soften && key !== 'black' && key !== 'white' ? softenColor(raw) : raw;
}

// لوگو، نام، رنگ‌ها و ساختار نمایشی تعیین‌شده توسط مدیر در «ظاهر و رنگ‌بندی» - در همه‌ی سامانه یکپارچه
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private availability = inject(AvailabilityService);
  private layout = inject(LayoutService);

  readonly settings = signal<AppSettings | null>(null);
  private request$?: Observable<AppSettings>;
  // تغییر حالت سیستم‌عامل برای حالت «بر اساس سیستم»
  private mode: 'panel' | 'customer' | null = null;

  constructor() {
    const mq = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : null;
    mq?.addEventListener?.('change', () => {
      if (this.mode === 'panel') this.applyPanel();
      if (this.mode === 'customer') this.applyCustomer();
    });
  }

  load(force = false): Observable<AppSettings> {
    if (!this.request$ || force) {
      this.request$ = this.availability.getSettings().pipe(
        tap((s) => {
          this.settings.set(s);
          this.applyIdentity(s.branding);
        }),
        shareReplay(1),
      );
    }
    return this.request$;
  }

  branding(): Branding | null {
    return this.settings()?.branding ?? null;
  }

  appName(): string {
    return this.branding()?.appName || 'سامانه رزرو';
  }

  logoUrl(): string | null {
    const url = this.branding()?.logoUrl;
    return url ? mediaUrl(url) : null;
  }

  color(key: ColorKey): string {
    const b = this.branding();
    return b ? resolveColor(b.colors, key, b.soften) : '#10b981';
  }

  // نام سامانه در عنوان مرورگر و لوگو به‌عنوان آیکن تب
  private applyIdentity(b: Branding) {
    if (!b) return;
    if (!document.title.includes(' - ')) document.title = b.appName;
    const logo = b.logoUrl ? mediaUrl(b.logoUrl) : null;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (logo) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = logo;
      link.removeAttribute('type');
    }
    this.applyColorVars(b);
    document.documentElement.style.fontSize = FONT_SIZES[b.fontSize] ?? FONT_SIZES.md;
  }

  // رنگ‌های سازمانی به‌صورت متغیر CSS برای سایدبار، صفحه‌ی ورود و ...
  private applyColorVars(b: Branding) {
    const root = document.documentElement.style;
    const keys: ColorKey[] = ['primary', 'secondary', 'accent1', 'accent2'];
    for (const key of keys) root.setProperty(`--brand-${key}`, resolveColor(b.colors, key, b.soften));
    // رنگ تاکیدی سایدبار: رنگ اختیاری اول، وگرنه رنگ اصلی
    root.setProperty('--brand-accent', resolveColor(b.colors, b.colors.accent1 ? 'accent1' : 'primary', b.soften));
  }

  // ---------------- پنل مدیریت و پرسنل (ظاهر یکسان) ----------------

  panelPrefs(): PanelPrefs {
    try {
      return JSON.parse(localStorage.getItem(PANEL_PREFS_KEY) || '{}');
    } catch {
      return {};
    }
  }

  savePanelPrefs(prefs: PanelPrefs) {
    try {
      localStorage.setItem(PANEL_PREFS_KEY, JSON.stringify({ ...this.panelPrefs(), ...prefs }));
    } catch {
      // فقط برای همین نشست
    }
    this.applyPanel();
  }

  resetPanelPrefs() {
    try {
      localStorage.removeItem(PANEL_PREFS_KEY);
    } catch {
      // ignore
    }
    this.applyPanel();
  }

  // ظاهر نهایی پنل = پیش‌فرض مدیر + انتخاب‌های همین دستگاه
  effectivePanel(b: Branding): PanelTheme {
    const prefs = this.panelPrefs();
    // انتخاب قدیمی (فقط darkMode) به حالت سه‌گانه تبدیل می‌شود
    const themeMode: ThemeMode = prefs.themeMode ?? (prefs.darkMode !== undefined ? (prefs.darkMode ? 'dark' : 'light') : (b.panel.themeMode ?? (b.panel.darkMode ? 'dark' : 'light')));
    return { ...b.panel, ...prefs, themeMode, darkMode: isDarkMode(themeMode) };
  }

  applyPanel() {
    this.mode = 'panel';
    this.load().subscribe((s) => {
      if (!s.branding) return;
      this.applyPanelTheme(this.effectivePanel(s.branding), s.branding);
    });
  }

  applyPanelTheme(theme: PanelTheme, b: Branding) {
    const preset = PRESETS[theme.preset] ?? Aura;
    const primary = resolveColor(b.colors, 'primary', b.soften);
    $t().preset(preset).preset(presetExtension(palette(primary), theme.preset)).use({ useDefaultOptions: true });
    this.applyIdentity(b);
    const dark = isDarkMode(theme.themeMode, theme.darkMode);
    applyColorScheme(dark);
    this.layout.layoutConfig.update((c) => ({ ...c, preset: theme.preset, menuMode: theme.menuMode, darkTheme: dark }));
  }

  // ---------------- صفحه‌ی مشتری ----------------

  customerDark(): boolean | null {
    try {
      const v = localStorage.getItem(CUSTOMER_DARK_KEY);
      return v == null ? null : v === '1';
    } catch {
      return null;
    }
  }

  setCustomerDark(dark: boolean) {
    try {
      localStorage.setItem(CUSTOMER_DARK_KEY, dark ? '1' : '0');
    } catch {
      // ignore
    }
    document.documentElement.classList.toggle('app-dark', dark);
    applyColorScheme(dark);
  }

  // حالت پیش‌فرض صفحه‌ی مشتری (روشن، تیره یا سیستم)؛ انتخاب خود مشتری مقدم است
  customerDefaultDark(b: Branding): boolean {
    return isDarkMode(b.customer.themeMode, b.customer.darkMode);
  }

  applyCustomer() {
    this.mode = 'customer';
    this.load().subscribe((s) => {
      const b = s.branding;
      if (!b) return;
      updatePrimaryPalette(palette(resolveColor(b.colors, b.customer.primary, b.soften)));
      const dark = !isLegacyBrowser() && (this.customerDark() ?? this.customerDefaultDark(b));
      document.documentElement.classList.toggle('app-dark', dark);
      applyColorScheme(dark);
      document.title = `${b.customer.title || b.appName} - رزرو آنلاین`;
    });
  }
}
